import ChatGPTService from '@src/services/ChatGPTService';

import type { AIPromptRepository } from '@src/repositories';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { TranscriptionStore } from '@src/stores-v2';
import type { Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

export type ScopedStory = Pick<Story, 'id' | 'title'> &
  Partial<Pick<Story, 'primaryShowcase'>>;

/**
 * A ChatGPTService bound to one story's transcript instead of the editor's.
 *
 * ChatGPTService reads the open story (`videoCreator.story`) for its id, title
 * and showcase, reads the transcript from the shared transcription store, and
 * streams its output into `videoCreator.contentStudioGeneratedContent`. Run for
 * a story that is not open — speaker identification during an upload — each
 * of those would be the wrong story's, or abort for want of an open one. This
 * view delegates everything else to the real store but carries its own story,
 * transcript and generated-content scratch space, so nothing it does lands on
 * the open story.
 *
 * Use a fresh instance per run: ChatGPTService's speaker identification swaps
 * methods on the instance while it streams.
 */
export class StoryScopedChatGPTService extends ChatGPTService {
  constructor(
    videoCreator: IVideoCreatorStore,
    aiPromptRepository: AIPromptRepository,
    story: ScopedStory,
    elements: TranscriptElement[],
  ) {
    const scopedCreator: IVideoCreatorStore = Object.create(videoCreator, {
      story: { value: story, enumerable: true },
      contentStudioGeneratedContent: {
        value: undefined,
        writable: true,
        enumerable: true,
      },
    });
    super(scopedCreator, aiPromptRepository);
    // Speaker identification only reads the original transcript.
    const transcript: Pick<
      TranscriptionStore,
      'originalTranscription' | 'getFinalTranscriptionElements'
    > = {
      originalTranscription: {
        elements,
      } as TranscriptionStore['originalTranscription'],
      getFinalTranscriptionElements: () => elements,
    };
    this.transcriptionStore = transcript as TranscriptionStore;
  }
}
